import semver from 'semver'
import versionService from './versionService'

export const DOCS_RELEASE_HOST = 'https://docs.yellowbrick.com'
export const DOCS_TESTER_HOST = 'https://tester.yellowbrick.com'

/**
 * Resolve the documentation host for a build.
 *
 * A build with a well-formed release version (parseable by
 * versionService.parseVersion, e.g. `7.4.5-123.abcd1234`) links to the
 * release host; a dev/local/unparseable build falls through to tester. An
 * explicit `?docsite=` override always wins.
 */
export const resolveDocHost = (buildVersion?: string | null, override?: string | null): string => {
  if (override) {
    return override
  }
  return versionService.parseVersion(buildVersion) ? DOCS_RELEASE_HOST : DOCS_TESTER_HOST
}

/**
 * Resolve the documentation path version segment for a build.
 *
 * Release builds pin to their own full patch segment (e.g. `7.4.5`); the docs
 * host serves full-patch directories only. Dev/local/unparseable builds keep
 * the legacy `latest` segment so the local docs-authoring loop is unchanged.
 */
export const resolveDocVersion = (buildVersion?: string | null): string => {
  const parsed = versionService.parseVersion(buildVersion)
  return parsed ? parsed.semver : 'latest'
}

/**
 * True when the build is a parseable release build. Callers gate all new
 * release-only behavior (e.g. version-line selection in help-search) on this.
 */
export const isReleaseBuild = (buildVersion?: string | null): boolean => {
  return !!versionService.parseVersion(buildVersion)
}

/**
 * Option B doc-version line selection for help-search.
 *
 * Release builds: from the published versions, pick the highest patch in the
 * build's own minor line (e.g. `7.4.x`), falling back to the build's exact
 * version segment if the line has no published entry. This never crosses to a
 * different minor line (so it can never link to e.g. `7.6.0`).
 *
 * Dev/unparseable builds: preserve legacy behavior and return the global
 * newest published version.
 */
export const selectDocVersion = (publishedVersions: string[], buildVersion?: string | null): string => {
  const valid = publishedVersions.filter(semver.valid).sort(semver.rcompare)
  const parsed = versionService.parseVersion(buildVersion)
  if (!parsed) {
    return valid[0]
  }
  const linePrefix = `${parsed.major}.${parsed.minor}.`
  const lineMatch = valid.find(v => v.startsWith(linePrefix))
  return lineMatch || parsed.semver
}

// Build-time resolved values for application (non-test) use. The buildVersion
// is injected only for production `make build`; it is unset under `yarn dev`.
const buildVersion = import.meta.env.VITE_BUILD_VERSION

/**
 * Compute the doc host honoring the current route's `?docsite=` override.
 * Read at call time so the route query is available.
 */
export const getDocHost = (override?: string | null): string =>
  resolveDocHost(buildVersion, override)

/**
 * The doc path version segment for this build.
 *
 * Deliberately NOT a function of the `?docsite=` override: the override
 * redirects the host only, while the version stays pinned to this build's own
 * version. So on a release build, `?docsite=https://tester.yellowbrick.com`
 * yields `tester/<patch>/...`, which may 404 if tester serves no matching
 * patch dir. The override is a developer/preview affordance and dev builds
 * already resolve to `latest`, so this edge case does not affect release users.
 */
export const docVersion = resolveDocVersion(buildVersion)

/** True when this build is a parseable release build. */
export const releaseBuild = isReleaseBuild(buildVersion)
